import { z } from "zod";
import { LossTypeSchema } from "./common.js";
// Spec 081 — o vocabulário do contador (papel, equivalência, refinamentos, projeção) mora em
// `parameters-counter.ts`: este arquivo chegou ao teto de 200 linhas efetivas com ele dentro.
import {
  COUNTER_ROLE_REFINE_MESSAGE,
  ProductionRoleSchema,
  UnitFactorSchema,
  counterRoleRefine,
} from "./parameters-counter.js";

export * from "./parameters-counter.js";

export const ParameterDataTypeSchema = z.enum([
  "number",
  "boolean",
  "enum",
  "text",
  "duration",
  "integer",
]);
export const ParameterSourceSchema = z.enum([
  "MANUAL",
  "SENSOR",
  "IMPORT",
  "INTEGRATION",
  "CALCULATED",
]);
export const RestrictionTypeSchema = z.enum(["FIXED", "VARIABLE", "PROBABILISTIC"]);

export const ParameterCategorySchema = z.enum([
  "STATUS",
  "NUMERIC",
  "COUNTER",
  "BOOLEAN",
  "TEXT",
  "DURATION",
]);
export type ParameterCategory = z.infer<typeof ParameterCategorySchema>;

// Spec 078 — papel visual do parâmetro na cena 3D. Declarado, nunca deduzido (D-2). Um por
// máquina para cada papel ≠ NONE (índice parcial).
//
// ⚠️ Spec 080 — `TEMPERATURE` NÃO nomeia mais uma grandeza medida: passou a significar "o sinal
// cujo disparo PARA esta máquina" (relé térmico). O valor do enum foi mantido de propósito
// (`spec.md` § Clarifications: renomear custaria um `ALTER TYPE ... RENAME VALUE`), então quem ler
// só o identificador vai modelar errado. Vale em `BOOLEAN` e `NUMERIC`, e só sobre parâmetro
// marcado como restrição operacional; `FLOW` continua só em `COUNTER`.
export const ParameterVisualRoleSchema = z.enum(["NONE", "FLOW", "TEMPERATURE"]);
export type ParameterVisualRole = z.infer<typeof ParameterVisualRoleSchema>;

// Spec 078 — regra de alarme por limite (qualquer NUMERIC). O limite vive aqui, nunca no papel.
// A faixa é a da COLUNA (`Decimal(12,3)`): sem ela um valor de 10 dígitos passa por toda a
// validação e estoura no Postgres (22003 numeric field overflow), que o handler de erro não
// traduz — 500 no lugar de um 400 dizendo qual campo.
const LIMIT_ABS_MAX = 999_999_999.999;
const LimitSchema = z.number().finite().min(-LIMIT_ABS_MAX).max(LIMIT_ABS_MAX);
export const limitsRefine = <T extends { limitMax?: number | null; limitMin?: number | null }>(
  b: T
): boolean =>
  b.limitMax === undefined ||
  b.limitMax === null ||
  b.limitMin === undefined ||
  b.limitMin === null ||
  b.limitMin < b.limitMax;
export const LIMITS_REFINE_MESSAGE: { message: string; path: (string | number)[] } = {
  message: "O limite mínimo deve ser menor que o máximo.",
  path: ["limitMin"],
};

export const MachineStatusSchema = z.enum(["RUNNING", "STOPPED", "IDLE", "NODATA"]);
export type MachineStatusValue = z.infer<typeof MachineStatusSchema>;

export const StatusMappingSchema = z
  .record(z.string().min(1).max(50), MachineStatusSchema)
  .refine((m) => Object.keys(m).length >= 1, {
    message: "statusMapping deve ter pelo menos 1 entry",
  });
export type StatusMapping = z.infer<typeof StatusMappingSchema>;

export const CreateStatusParameterSchema = z.object({
  category: z.literal("STATUS"),
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional(),
  source: ParameterSourceSchema.refine((s) => ["SENSOR", "INTEGRATION", "MANUAL"].includes(s), {
    message: "STATUS aceita apenas SENSOR, INTEGRATION ou MANUAL",
  }),
  statusMapping: StatusMappingSchema,
  // Só parâmetros STATUS dirigem a transição STOPPED → alarme. default(true).
  generatesAlarm: z.boolean().default(true),
});

export const CreateNumericParameterSchema = z.object({
  category: z.literal("NUMERIC"),
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional(),
  unit: z.string().min(1).max(20),
  source: ParameterSourceSchema,
  // Spec 080 — marcado como restrição, sair da faixa `limitMin`/`limitMax` PARA a máquina (não só
  // alarma). Sem limite nenhum a marca é recusada: sem faixa não há o que violar (FR-001c).
  isRestriction: z.boolean().default(false),
  restrictionType: RestrictionTypeSchema.optional(),
  // Spec 080 — TEMPERATURE acende o alerta de PARADA TÉRMICA na cena; exige `isRestriction`.
  visualRole: z.enum(["NONE", "TEMPERATURE"]).optional(),
  limitMax: LimitSchema.nullable().optional(),
  limitMin: LimitSchema.nullable().optional(),
});

export const CreateCounterParameterSchema = z.object({
  category: z.literal("COUNTER"),
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional(),
  unit: z.string().min(1).max(20),
  source: ParameterSourceSchema.refine(
    (s) => ["SENSOR", "MANUAL", "IMPORT", "CALCULATED"].includes(s),
    { message: "COUNTER aceita SENSOR, MANUAL, IMPORT ou CALCULATED" }
  ),
  // Spec 081 — o sentido deste contador. Ausente = contador comum, sem papel na produção.
  // Imutável após a criação (NÃO entra em UpdateParameterRequestSchema): trocar o sentido
  // reinterpretaria toda a história já lançada do contador.
  productionRole: ProductionRoleSchema.optional(),
  // Qual produto ele conta. Opcional, mas exigido para citar o contador num indicador por produto.
  productId: z.string().uuid().optional(),
  // Exigido sse `productionRole = "SCRAPS"` — um sensor não escolhe o tipo por evento.
  lossType: LossTypeSchema.optional(),
  // Sobreposição da equivalência do produto. A regra que a limita à unidade diferente depende do
  // banco e vive no service (`PARAMETER_FACTOR_SAME_UNIT`).
  unitFactor: UnitFactorSchema.optional(),
  // Spec 078 — FLOW dita a velocidade do material nos trechos ditados por esta máquina.
  visualRole: z.enum(["NONE", "FLOW"]).optional(),
});
export const CreateBooleanParameterSchema = z.object({
  category: z.literal("BOOLEAN"),
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional(),
  source: ParameterSourceSchema.refine((s) => ["SENSOR", "MANUAL", "INTEGRATION"].includes(s), {
    message: "BOOLEAN aceita SENSOR, MANUAL ou INTEGRATION",
  }),
  // Spec 080 — a marca deixou de ser exclusiva do numérico: é o booleano do relé térmico que a
  // fábrica tem. Marcado, o valor `1`/`true` PARA a máquina (FR-001a).
  isRestriction: z.boolean().default(false),
  // Spec 080 — acende o alerta de parada térmica na cena; exige `isRestriction` (FR-004a).
  visualRole: z.enum(["NONE", "TEMPERATURE"]).optional(),
});

export const CreateTextParameterSchema = z.object({
  category: z.literal("TEXT"),
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional(),
  source: ParameterSourceSchema.refine((s) => ["MANUAL", "IMPORT", "INTEGRATION"].includes(s), {
    message: "TEXT aceita MANUAL, IMPORT ou INTEGRATION",
  }),
});

export const CreateDurationParameterSchema = z.object({
  category: z.literal("DURATION"),
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional(),
  unit: z.enum(["s", "min", "h"]),
  source: ParameterSourceSchema.refine(
    (s) => ["SENSOR", "MANUAL", "IMPORT", "CALCULATED"].includes(s),
    { message: "DURATION aceita SENSOR, MANUAL, IMPORT ou CALCULATED" }
  ),
});

export const CreateParameterRequestSchema = z
  .discriminatedUnion("category", [
    CreateStatusParameterSchema,
    CreateNumericParameterSchema,
    CreateCounterParameterSchema,
    CreateBooleanParameterSchema,
    CreateTextParameterSchema,
    CreateDurationParameterSchema,
  ])
  .refine((b) => (b.category === "NUMERIC" ? limitsRefine(b) : true), LIMITS_REFINE_MESSAGE)
  .refine(counterRoleRefine, COUNTER_ROLE_REFINE_MESSAGE);
export type CreateParameterRequest = z.infer<typeof CreateParameterRequestSchema>;

export const UpdateParameterRequestBaseSchema = z.object({
  category: ParameterCategorySchema.optional(),
  name: z.string().min(1).max(120).optional(),
  description: z.string().max(500).optional(),
  unit: z.string().max(40).nullable().optional(),
  source: ParameterSourceSchema.optional(),
  influencesSimulation: z.boolean().optional(),
  isRestriction: z.boolean().optional(),
  restrictionType: RestrictionTypeSchema.optional(),
  statusMapping: StatusMappingSchema.optional(),
  // Editável (silencia/religa o alarme do parâmetro STATUS).
  generatesAlarm: z.boolean().optional(),
  // Spec 078 — papel visual é MUTÁVEL (trocar de parâmetro é o caso de uso); limites idem.
  visualRole: ParameterVisualRoleSchema.optional(),
  limitMax: LimitSchema.nullable().optional(),
  limitMin: LimitSchema.nullable().optional(),
  // Spec 081 — `productionRole` NÃO entra aqui, seguindo o precedente do booleano que ele
  // substitui. Produto, tipo de perda e equivalência entram: corrigir a que produto o contador se
  // refere é conserto de cadastro, não reinterpretação da história.
  productId: z.string().uuid().nullable().optional(),
  lossType: LossTypeSchema.nullable().optional(),
  unitFactor: UnitFactorSchema.nullable().optional(),
});
export const UpdateParameterRequestSchema = UpdateParameterRequestBaseSchema.refine(
  limitsRefine,
  LIMITS_REFINE_MESSAGE
);
export type UpdateParameterRequest = z.infer<typeof UpdateParameterRequestSchema>;

export const LinkSensorRequestSchema = z.object({
  sensorId: z.string().uuid(),
});
export type LinkSensorRequest = z.infer<typeof LinkSensorRequestSchema>;

export const DeleteParameterConflictDetailsSchema = z.object({
  linkedSensorIds: z.array(z.string().uuid()),
});
export type DeleteParameterConflictDetails = z.infer<typeof DeleteParameterConflictDetailsSchema>;

export const RegisterStatusEntryRequestSchema = z.object({
  value: z.string().min(1).max(50),
  occurredAt: z.string().datetime({ offset: true }),
  observation: z.string().max(500).optional(),
});
export type RegisterStatusEntryRequest = z.infer<typeof RegisterStatusEntryRequestSchema>;

export const RegisterStatusEntryResponseDataSchema = z.object({
  machineEventId: z.string().uuid().nullable(),
  machineStatus: MachineStatusSchema,
  occurredAt: z.string().datetime({ offset: true }),
  productionRunId: z.string().uuid().nullable(),
  noop: z.boolean().default(false),
});
export type RegisterStatusEntryResponseData = z.infer<typeof RegisterStatusEntryResponseDataSchema>;

// Registro manual de valor de parâmetro (categorias ≠ STATUS;
// STATUS continua em /machines/:machineId/status-entry). Validação por categoria no service:
// NUMERIC decimal finito | COUNTER inteiro ≥0 | BOOLEAN "true"/"false" | TEXT 1..500 | DURATION ≥0.
export const RegisterParameterEntryRequestSchema = z.object({
  value: z.string().min(1).max(500),
});
export type RegisterParameterEntryRequest = z.infer<typeof RegisterParameterEntryRequestSchema>;

export const RegisterParameterEntryResponseDataSchema = z.object({
  entryId: z.string().uuid(),
  parameterId: z.string().uuid(),
  value: z.string(),
  recordedAt: z.string().datetime({ offset: true }),
  recordedBy: z.object({ id: z.string().uuid(), name: z.string() }),
  productionRunId: z.string().uuid().nullable(),
});
export type RegisterParameterEntryResponseData = z.infer<
  typeof RegisterParameterEntryResponseDataSchema
>;
